/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { RenameModal } from 'src/core/components/RenameModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { QnaAddChitchatSourceModal } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerAddChitchatSourceModal';
import { QnaAddFileSourceModal, QnaFileSourceDto } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerAddFileSourceModal';
import { QnaAddUrlSourceModal, QnaUrlSourceDto } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerAddUrlSourceModal';
import { QnaManageSourcesCommandBar } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerManageSourcesCommandBar';
import { QnaManageSourcesTable } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerManageSourcesTable';
import { QnaMakerSourceContentStructureKind, QnaMakerSourceType } from 'src/qnaMaker/core/services/sources/qnaMakerSourcesSwaggers';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';
import { chitchatUrlMap, QnaSourceChitchatTypes } from 'src/qnaMaker/modules/utils/chitchatTypeToFileUrl';

export type SourcesModalTypes = 'url' | 'file' | 'chitchat';
export type SourcesFiltersTypes = { sourceKind: QnaMakerSourceType[]; contentStructureKind: QnaMakerSourceContentStructureKind[] };

const messages = defineMessages({
    pageTitle: {
        id: 'qnaMaker.qnaManageSourcesPage.pageTitle',
        defaultMessage: 'Manage sources'
    },
    emptyTablePlaceholder: {
        id: 'qnaMaker.qnaManageSourcesPage.emptyTablePlaceholder',
        defaultMessage: "You don't have any sources yet."
    },
    deleteSourceTitle: {
        id: 'qnaMaker.qnaManageSourcesPage.deleteSourceTitle',
        defaultMessage: 'Delete selected source(s)?'
    },
    deleteSourceBody: {
        id: 'qnaMaker.qnaManageSourcesPage.deleteSourceBody',
        defaultMessage: 'Are you sure you want to delete the selected source(s)?'
    },
    editSourceName: {
        id: 'qnaMaker.qnaManageSourcesPage.editSourceName',
        defaultMessage: 'Edit source name'
    },
    emptyStateTitle: {
        id: 'qnaMaker.qnaManageSourcesPage.emptyStateTitle',
        defaultMessage: 'Add sources to your knowledge base'
    },
    emptyStateBody: {
        id: 'qnaMaker.qnaManageSourcesPage.emptyStateBody',
        defaultMessage:
            'Extract question-and-answer pairs from an online FAQ, product manuals, or add unstructured content to enable search over text. Supported formats are .tsv, .pdf, .doc, .docx, .xlsx.'
    },
    emptyStateLearnMore: {
        id: 'qnaMaker.qnaManageSourcesPage.emptyStateLearnMore',
        defaultMessage: 'Learn more about knowledge base sources.'
    },
    addingSourcesToast: {
        id: 'qnaMaker.qnaManageSourcesPage.addingSourcesToast',
        defaultMessage: 'Adding sources...'
    },
    refreshingUrlToastTitle: {
        id: 'qnaMaker.qnaManageSourcesPage.refreshUrlToastTitle',
        defaultMessage: 'Refreshing URL(s)...'
    },
    refreshingUrlCompletedToastContent: {
        id: 'qnaMaker.qnaManageSourcesPage.refreshingUrlCompletedToastContent',
        defaultMessage: 'URL(s) refreshed successfully.'
    },
    refreshingUrlFailedToastContent: {
        id: 'qnaMaker.qnaManageSourcesPage.refreshingUrlFailedToastContent',
        defaultMessage: 'Server error occurred while refreshing URL(s).'
    }
});

const defaultModalsState = {
    isDeleteSourceModalHidden: true,
    isRenameSourceModalHidden: true,
    isAddUrlSourceModalHidden: true,
    isAddFileModalHidden: true,
    isAddChitchatModalHidden: true
};

const { styleDiv } = getStylistV2('QnaManageSourcesPage');
const ManageSourcesTableWindow = styleDiv('ManageSourcesTableWindow', { height: '100%', overflowY: 'auto' });

export const QnaManageSourcesPage = observer(() => {
    const { rootStore, dispatcher, uiStore } = useGlobalState();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { isAccessibleMode } = useZoomLevel();
    const history = useHistory();
    const { selection, selectedItems } = useSelection<QnaMakerSourceStore>();
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const [query, setQuery] = React.useState('');
    const [sortKey, setSortKey] = React.useState<SortKeyType<QnaMakerSourceStore>>({ key: 'source', order: 'desc' });
    const [filteredKeys, setFilteredKeys] = React.useState<SourcesFiltersTypes>({ sourceKind: [], contentStructureKind: [] });
    const { sourcesStoreHouse, id: currentProjectId } = rootStore.currentQnaMakerProjectStore;
    const itemsTracker = rootStore.currentQnaMakerProjectStore.sourcesStoreHouse.tracker;
    const isAddingChitchatDisabled = !chitchatUrlMap[rootStore.currentQnaMakerProjectStore.language];
    const tableRef = React.createRef<HTMLDivElement>();
    const isSourceLinkAttributeName = React.useRef<string>('is-source-link');
    const [isSourceAdded, updateIsSourceAdded] = React.useState<boolean>(false);

    React.useEffect(() => () => dequeueTeachingBubble('FirstSourceCreated'), []);

    React.useEffect(() => {
        if (tableRef.current && !uiStore.teachingBubbles.FirstSourceCreated) {
            const firstSourceLink = tableRef.current.querySelector(`[${isSourceLinkAttributeName.current}="true"]`);
            if (firstSourceLink && isSourceAdded && itemsTracker.isCompleted) {
                createTeachingBubble(
                    'FirstSourceCreated',
                    localize(qnaMakerTeachingBubbleMessages.firstSourceCreatedBody),
                    localize(qnaMakerTeachingBubbleMessages.firstSourceCreatedTitle),
                    firstSourceLink,
                    true,
                    true
                );
            }
        }
    }, [tableRef, Object.values(uiStore.teachingBubbles), itemsTracker.isCompleted]);

    const opTracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
        }
    });

    React.useEffect(() => {
        if (rootStore.serviceKey.value) {
            dispatcher.dispatch('getQnaMakerSources', { context: { forceRefresh: true } });
        }
        // The account id is added to the dependency list as in AAD authentication the service key has same value regardless of changing the resource.
    }, [rootStore.serviceKey.value, rootStore.azureStore.session.csAccountStore?.id]);

    const filteredSources = orderBy(
        substringSearchStrategy(sourcesStoreHouse.items, query, ['source', 'name']),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    )
        .filter(src => (filteredKeys.sourceKind.length ? filteredKeys.sourceKind.includes(src.kind) : src))
        .filter(src =>
            filteredKeys.contentStructureKind.length ? filteredKeys.contentStructureKind.includes(src.contentStructureKind) : src
        );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredSources);

    const handleAddingUrl = (sourcesDto: QnaUrlSourceDto[]) => {
        dispatcher.dispatch('addQnaMakerUrlSources', {
            sources: sourcesDto,
            tracker: opTracker,
            toastData: { running: { title: localize(messages.addingSourcesToast) } }
        });
        updateIsSourceAdded(true);
        setModalsState(defaultModalsState);
    };

    const handleAddingFile = (sources: QnaFileSourceDto[]) => {
        dispatcher.dispatch('addQnaMakerFileSources', {
            sources,
            tracker: opTracker,
            toastData: { running: { title: localize(messages.addingSourcesToast) } }
        });
        updateIsSourceAdded(true);
        setModalsState(defaultModalsState);
    };

    const handleAddingChitchat = (chitchatType: QnaSourceChitchatTypes) => {
        dispatcher.dispatch('addQnaMakerChitchatSources', {
            chitchatType,
            tracker: opTracker,
            toastData: { running: { title: localize(messages.addingSourcesToast) } }
        });
        updateIsSourceAdded(true);
        setModalsState(defaultModalsState);
    };

    const deleteSource = () => {
        dispatcher.dispatch('deleteQnaMakerSources', {
            sourcesIds: selectedItems.map(i => i.id),
            tracker: opTracker,
            toastData: {}
        });
    };

    const renameSource = ({ name }: { name: string }) => {
        const sourceId = selectedItems[0].id;
        dispatcher.dispatch('renameQnaMakerSources', {
            newName: name,
            sourceId,
            tracker: opTracker,
            toastData: {}
        });
    };

    const handleRefreshingURL = () => {
        dispatcher.dispatch('refreshQnaMakerSourceURLs', {
            sources: selectedItems,
            tracker: opTracker,
            toastData: {
                running: {
                    title: localize(messages.refreshingUrlToastTitle)
                },
                completed: {
                    title: localize(messages.refreshingUrlToastTitle),
                    content: localize(messages.refreshingUrlCompletedToastContent)
                },
                failed: {
                    title: localize(messages.refreshingUrlToastTitle),
                    content: localize(messages.refreshingUrlFailedToastContent)
                }
            }
        });

        logButtonClick(serviceName, 'Sources', 'Refreshurl', null, currentProjectId);
    };

    const onSortColumn = (column: IColumn) => {
        setSortKey({ key: column.key as keyof QnaMakerSourceStore, order: column.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const handleAddSourceModal = (sourceKind: SourcesModalTypes) => {
        switch (sourceKind) {
            case 'file':
                logButtonClick(serviceName, 'Sources', 'AddFiles', null, currentProjectId);
                setModalsState({ isAddFileModalHidden: false });
                break;
            case 'chitchat':
                logButtonClick(serviceName, 'Sources', 'AddChitchat', null, currentProjectId);
                setModalsState({ isAddChitchatModalHidden: false });
                break;
            case 'url':
            default:
                logButtonClick(serviceName, 'Sources', 'AddUrls', null, currentProjectId);
                setModalsState({ isAddUrlSourceModalHidden: false });
                break;
        }
    };

    const handleRenameSourceModal = () => {
        logButtonClick(serviceName, 'Sources', 'Editsourcename', null, currentProjectId);
        setModalsState({ isRenameSourceModalHidden: false });
    };

    const handleDeleteSourceModal = () => {
        logButtonClick(serviceName, 'Sources', 'Deletesource', null, currentProjectId);
        setModalsState({ isDeleteSourceModalHidden: false });
    };

    const handleSourceClick = (sourceId: string) => {
        history.push(`${routeTable.qnaMaker.editPage(currentProjectId)}?assets=qnas&source=${sourceId}`);
    };

    const EmptyStateMessage = (
        <Stack horizontalAlign="center">
            <LuisText variant="xLarge" fontWeight={600}>
                {localize(messages.emptyStateTitle)}
            </LuisText>
            <br />
            <LuisText styles={{ root: { maxWidth: '650px' } }} style={{ textAlign: 'center' }}>
                {localize(messages.emptyStateBody)} &nbsp;
                <ExternalLink href="https://aka.ms/qa-data-sources">{localize(messages.emptyStateLearnMore)}</ExternalLink>
            </LuisText>
        </Stack>
    );

    return (
        <Page size="container-fluid" pageName="KnowledgeBasesPage" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.pageTitle)} />

            <Stack verticalFill>
                <QnaManageSourcesCommandBar
                    query={query}
                    onDownloadSource={noop}
                    onQueryChange={setQuery}
                    filteredKeys={filteredKeys}
                    selectedItems={selectedItems}
                    onRefreshURL={handleRefreshingURL}
                    onAddSources={handleAddSourceModal}
                    itemsLength={filteredSources.length}
                    onChangeFilterKeys={setFilteredKeys}
                    areActionsDisabled={opTracker.isRunning}
                    isAddingChitchatDisabled={isAddingChitchatDisabled}
                    onDeleteSource={handleDeleteSourceModal}
                    onRenameSource={handleRenameSourceModal}
                    isRefreshUrlDisabled={
                        !(
                            selectedItems.length &&
                            selectedItems.reduce(
                                (accumulator, item) => accumulator && item.kind === 'url' && item.contentStructureKind === 'semistructured',
                                true
                            )
                        )
                    }
                />

                {itemsTracker.isRunning || sourcesStoreHouse.items.length ? (
                    <Stack.Item>
                        <div ref={tableRef}>
                            <ManageSourcesTableWindow data-is-scrollable={!isAccessibleMode}>
                                <QnaManageSourcesTable
                                    isSourceLinkAttributeName={isSourceLinkAttributeName.current}
                                    sortKey={sortKey}
                                    selection={selection}
                                    items={pageItems}
                                    isLoading={itemsTracker.isRunning}
                                    areItemsFiltered={Boolean(
                                        query || filteredKeys.sourceKind.length || filteredKeys.contentStructureKind.length
                                    )}
                                    overallItemsCount={filteredSources.length}
                                    emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
                                    onSortColumn={onSortColumn}
                                    onSourceClick={handleSourceClick}
                                />
                            </ManageSourcesTableWindow>
                        </div>
                        <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />
                    </Stack.Item>
                ) : (
                    <EmptyStateView message={EmptyStateMessage} />
                )}
            </Stack>

            <DeletePromptModal
                onDelete={deleteSource}
                disabled={opTracker.isRunning}
                hidden={modalsState.isDeleteSourceModalHidden}
                dialogContentProps={{ title: localize(messages.deleteSourceTitle) }}
                onDismiss={() => setModalsState({ isDeleteSourceModalHidden: true })}>
                <LuisText>{localize(messages.deleteSourceBody)}</LuisText>
            </DeletePromptModal>

            <RenameModal
                onSubmit={renameSource}
                name={selectedItems[0]?.name || ''}
                disabled={opTracker.isRunning}
                hidden={modalsState.isRenameSourceModalHidden}
                dialogContentProps={{ title: localize(messages.editSourceName) }}
                onDismiss={() => setModalsState({ isRenameSourceModalHidden: true })}
            />

            <QnaAddUrlSourceModal
                onSubmit={handleAddingUrl}
                hidden={modalsState.isAddUrlSourceModalHidden}
                onDismiss={() => setModalsState({ isAddUrlSourceModalHidden: true })}
            />

            <QnaAddFileSourceModal
                onSubmit={handleAddingFile}
                hidden={modalsState.isAddFileModalHidden}
                onDismiss={() => setModalsState({ isAddFileModalHidden: true })}
            />

            <QnaAddChitchatSourceModal
                onSubmit={handleAddingChitchat}
                hidden={modalsState.isAddChitchatModalHidden}
                onDismiss={() => setModalsState({ isAddChitchatModalHidden: true })}
            />
        </Page>
    );
});
